import * as z from 'zod';
import { contentTypeSchema } from '../enums/content.js';
import { dayOfWeekSchema, durationSecondsSchema, instantSchema, timeOfDaySchema, } from '../primitives/time.js';
/**
 * A span of permitted wall-clock time within a single day.
 *
 * Windows may not wrap past midnight. An overnight allowance is expressed as
 * two windows on two days, which keeps every comparison a simple one and
 * removes wrap-around arithmetic from the policy engine.
 */
export const timeWindowSchema = z
    .object({
    start: timeOfDaySchema,
    end: timeOfDaySchema,
})
    .refine((window) => window.start < window.end, {
    message: 'start must precede end; express overnight spans as two windows',
});
/**
 * Permitted hours per weekday.
 *
 * A day with no entry permits nothing. An empty schedule therefore denies
 * everything, which is the correct default for this product.
 */
export const scheduleSchema = z.object({
    windowsByDay: z.partialRecord(dayOfWeekSchema, z.array(timeWindowSchema)),
});
export const screenTimeBudgetSchema = z.object({
    /** Ceiling across every content type combined. */
    dailyTotalSeconds: durationSecondsSchema,
    /** Longest uninterrupted stretch before a rest is required. */
    maxSessionSeconds: durationSecondsSchema,
    /** How long the screen must stay off once a session limit is reached. */
    restPeriodSeconds: durationSecondsSchema,
    /**
     * Optional per-type ceilings. A type with no entry is bounded only by
     * `dailyTotalSeconds`.
     */
    perContentTypeSeconds: z.partialRecord(contentTypeSchema, durationSecondsSchema),
});
/** Consumption so far within the current budget day. */
export const screenTimeSpentSchema = z.object({
    totalSeconds: durationSecondsSchema,
    byContentType: z.partialRecord(contentTypeSchema, durationSecondsSchema),
});
/**
 * A rest period in progress.
 *
 * Distinct from a movement break: a rest keeps the screen off for a while,
 * a movement break asks the child to do something and then resumes. SPEC.md
 * used one word for both, which this split removes.
 */
export const restStateSchema = z.object({
    startedAt: instantSchema,
    endsAt: instantSchema,
});
//# sourceMappingURL=screen-time.js.map